import { useEffect, useState, useCallback, useRef } from "react";

import { type CodeSubHintResponse, HintsClient, getHintsClient } from "sj-ui-toolkit/src/client";

// Define the return type of the hook
export interface UseAiHintsResult {
  aiHintsEnabled: boolean;
  error: Error | null;
  hints: CodeSubHintResponse[];
  isLoading: boolean;
  isLoaded: boolean;
  generateAiHint: (submissionId: string) => Promise<CodeSubHintResponse | null>;
  provideHintFeedback: (params: {
    submissionId: string;
    helpful: boolean;
    comment?: string;
  }) => Promise<void>;
  refetchHints: () => Promise<void>;
}

/**
 * Hook to manage AI hints for a lesson.
 *
 * @param tenant - The tenant identifier
 * @param lessonKey - The lesson key/ID
 * @returns Object containing hints, loading state, error state, and refetch function
 */
export const useAiHintsInternal = (
  tenant: string,
  lessonKey: string,
  isGuest: boolean
): UseAiHintsResult => {
  // Client
  const hintsClient: HintsClient = getHintsClient();

  // State
  const isInitializedRef = useRef(false);
  const [hints, setHints] = useState<CodeSubHintResponse[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const hintSettingsLoaded = useRef(false);
  const [aiHintsEnabled, setAiHintsEnabled] = useState<boolean>(!isGuest);

  /**
   * On mount, check to see if hints are enabled for the tenant
   */
  useEffect(() => {
    const checkHintsEnabled = async () => {
      try {
        const { response, data } = await hintsClient.checkIfHintsAreEnabled({ tenant });
        if (response?.status !== 200) {
          throw new Error(
            `Failed to check if hints are enabled: ${response?.status} ${response?.statusText}`
          );
        }
        setAiHintsEnabled(data?.enabled ?? false);
      } catch (err) {
        console.error("Error checking if hints are enabled:", err);
        setAiHintsEnabled(false);
      } finally {
        hintSettingsLoaded.current = true;
      }
    };

    checkHintsEnabled();
  }, [hintsClient, tenant]);

  /**
   * Loads AI hints from the hints service
   */
  const loadAiHints = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { response, data } = await hintsClient.retrieveAllHints({
        tenant,
        lessonKey,
      });

      if (response?.status !== 200) {
        throw new Error(`Failed to retrieve hints: ${response?.status} ${response?.statusText}`);
      }

      const loadedHints = data?.hints ?? [];
      setHints(loadedHints);
      return loadedHints;
    } catch (err) {
      console.error("Error loading AI hints:", err);
      const errorObject = err instanceof Error ? err : new Error(String(err));
      setError(errorObject);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [hintsClient, tenant, lessonKey]);

  /**
   * Generates a new AI hint for a given code submission ID.
   */
  const generateAiHint = useCallback(
    async (submissionId: string) => {
      setError(null);
      try {
        const { response, data } = await hintsClient.generateHint({
          tenant,
          lessonKey,
          submissionId,
        });

        if (response?.status !== 200) {
          throw new Error(`Failed to generate hint: ${response?.status} ${response?.statusText}`);
        }

        const newHint = data?.hint;
        if (newHint) {
          const hintResponse = {
            createdAt: data?.createdAt ?? new Date().toISOString(),
            submissionId,
            hint: newHint,
          };
          setHints(prevHints => [...prevHints, hintResponse]);
          return hintResponse;
        }
        return null;
      } catch (err) {
        console.error("Error generating AI hint:", err);
        const errorObject = err instanceof Error ? err : new Error(String(err));
        setError(errorObject);
        return null;
      }
    },
    [hintsClient, tenant, lessonKey]
  );

  /**
   * Provides feedback for a specific hint submission.
   */
  const provideHintFeedback = useCallback(
    ({
      submissionId,
      helpful,
      comment,
    }: {
      submissionId: string;
      helpful: boolean;
      comment?: string;
    }) => {
      return hintsClient.provideHintFeedback({
        tenant,
        submissionId,
        helpful,
        comment,
      });
    },
    [hintsClient, tenant]
  );

  /**
   * Public function to manually refetch hints
   */
  const refetchHints = useCallback(async () => {
    await loadAiHints();
  }, [loadAiHints]);

  // Load hints on mount or when tenant/lessonKey changes
  // Don't load for guest users
  useEffect(() => {
    const initialize = async () => {
      await loadAiHints();
    };

    if (!isInitializedRef.current && aiHintsEnabled && hintSettingsLoaded.current) {
      initialize();
      isInitializedRef.current = true;
    }
  }, [loadAiHints, tenant, lessonKey, aiHintsEnabled, hintSettingsLoaded.current]);

  return {
    aiHintsEnabled,
    error,
    hints,
    isLoading,
    isLoaded: isInitializedRef.current && !isLoading,
    generateAiHint,
    provideHintFeedback,
    refetchHints,
  };
};
